/**
 * =========================================================================================
 *   Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *   Module       : components/errors/CauseClassBadge
 *   Feature      : 252 - Backup Error Visibility & Guided Remediation
 *   Description  : The attribution model made visible (FR-004) — at a glance, WHOSE action a
 *                  given error requires.
 *
 *                  🛑 The model is honest in BOTH directions. `product_defect` is attributed to
 *                  us and styled as ours; a one-way blame model would erode the trust the whole
 *                  positioning depends on.
 *   Author       : Peter Dobler (Dobler Data Solutions)
 * =========================================================================================
 */
import React from 'react';
import { Badge } from '@/components/ui';
import type { CauseClass, ErrorStatus } from '../../api/backupErrors';

type Variant = 'default' | 'warning' | 'danger' | 'info' | 'success';

export const CAUSE_META: Record<CauseClass, { label: string; variant: Variant; owner: string }> = {
  configuration: { label: 'Configuration', variant: 'warning', owner: 'Your Fabric/Azure configuration — fixable by an operator' },
  platform_limitation: { label: 'Platform limit', variant: 'info', owner: 'A documented Microsoft Fabric limitation' },
  data_issue: { label: 'Data issue', variant: 'danger', owner: 'Something about the source data or the captured set' },
  resource_scale: { label: 'Scale / resources', variant: 'warning', owner: 'The entity outgrew the job’s memory or time budget' },
  transient: { label: 'Transient', variant: 'info', owner: 'A passing network or service condition — the next run clears it' },
  product_defect: { label: 'Product defect', variant: 'danger', owner: 'Ours — routed to the platform team' },
  needs_triage: { label: 'Needs triage', variant: 'default', owner: 'Cause not yet determined — follow the diagnostic' },
};

export const STATUS_META: Record<ErrorStatus, { label: string; variant: Variant }> = {
  failing: { label: 'Failing', variant: 'danger' },
  at_risk: { label: 'At risk', variant: 'warning' },
  resolved: { label: 'Resolved', variant: 'success' },
  accepted_limitation: { label: 'Accepted limit', variant: 'info' },
};

export const CauseClassBadge: React.FC<{ causeClass: CauseClass }> = ({ causeClass }) => {
  const meta = CAUSE_META[causeClass] ?? CAUSE_META.needs_triage;
  return <Badge variant={meta.variant} title={meta.owner}>{meta.label}</Badge>;
};

export const StatusBadge: React.FC<{ status: ErrorStatus }> = ({ status }) => {
  const meta = STATUS_META[status] ?? STATUS_META.failing;
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
};

export default CauseClassBadge;
